import { Navigate, Outlet, createBrowserRouter } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Result, Button } from "antd";
import { AppShell } from "@/layouts/AppShell";
import { AuthLayout } from "@/layouts/AuthLayout";
import { PlatformShell } from "@/layouts/PlatformShell";
import { useAuthStore } from "@/core/auth/authStore";
import { LoadingScreen } from "@/shared/components/LoadingScreen";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { WorkspaceDashboardPage } from "@/features/workspace/pages/WorkspaceDashboardPage";
import { LeadsPage } from "@/features/leads/pages/LeadsPage";
import { LeadIntelligencePage } from "@/features/leads/pages/LeadIntelligencePage";
import { PublicLeadCapturePage } from "@/features/leads/pages/PublicLeadCapturePage";
import { ProfilePage } from "@/features/profile/pages/ProfilePage";
import { ForbiddenPage } from "@/features/system/pages/ForbiddenPage";
import { NotFoundPage } from "@/features/system/pages/NotFoundPage";
import { hasPermission } from "@/core/auth/permissions";
import { ContactsPage } from "@/features/crm/pages/ContactsPage";
import { AccountsPage } from "@/features/crm/pages/AccountsPage";
import { OpportunitiesPage } from "@/features/sales/pages/OpportunitiesPage";
import { B2bSalesPage } from "@/features/sales/pages/B2bSalesPage";
import { ActivitiesPage } from "@/features/activities/pages/ActivitiesPage";
import { TeamPage } from "@/features/team/pages/TeamPage";
import { TenantRolesPage } from "@/features/team/pages/TenantRolesPage";
import { OrganizationPage } from "@/features/organization/pages/OrganizationPage";
import { PublicOrganizationPage } from "@/features/organization/pages/PublicOrganizationPage";
import { CatalogPage } from "@/features/catalog/pages/CatalogPage";
import { AutomationsPage } from "@/features/automations/pages/AutomationsPage";
import { CustomerSmsPage } from "@/features/customerSms/pages/CustomerSmsPage";
import { RetailSalesPage } from "@/features/retail/pages/RetailSalesPage";
import { PublicPersonalLandingPage } from "@/features/retail/pages/PublicPersonalLandingPage";
import { ReservationSettingsPage } from "@/features/retail/pages/ReservationSettingsPage";
import { RetailSaleSettingsPage } from "@/features/retail/pages/RetailSaleSettingsPage";
import { RetailLoyaltyPage } from "@/features/retail/pages/RetailLoyaltyPage";
import { RestaurantPosPage } from "@/features/restaurant/pages/RestaurantPosPage";
import { RestaurantMenuPage } from "@/features/restaurant/pages/RestaurantMenuPage";
import { PublicRetailReceiptPage } from "@/features/retail/pages/PublicRetailReceiptPage";
import { CampaignsPage } from "@/features/campaigns/pages/CampaignsPage";
import { PlatformLoginPage } from "@/features/platform/pages/PlatformLoginPage";
import { PlatformTenantsPage } from "@/features/platform/pages/PlatformTenantsPage";
import { PlatformNotificationsPage } from "@/features/platform/pages/PlatformNotificationsPage";
import { PlatformMessageTemplatesPage } from "@/features/platform/pages/PlatformMessageTemplatesPage";
import { PlatformSettingsPage } from "@/features/platform/pages/PlatformSettingsPage";
import { PlatformStaffPage } from "@/features/platform/pages/PlatformStaffPage";
import { PlatformAnnouncementsPage } from "@/features/platform/pages/PlatformAnnouncementsPage";
import { PlatformBaseDataPage } from "@/features/platform/pages/PlatformBaseDataPage";
import { PlatformFeaturesPage } from "@/features/platform/pages/PlatformFeaturesPage";
import { PlatformSupportPage } from "@/features/platform/pages/PlatformSupportPage";
import { PlatformGuidesPage } from "@/features/platform/pages/PlatformGuidesPage";
import { TenantSupportPage } from "@/features/support/pages/TenantSupportPage";
import { GuidanceCenterPage } from "@/features/guidance/pages/GuidanceCenterPage";
import { TenantBillingPage } from "@/features/billing/pages/TenantBillingPage";
import { PlatformBillingRequestsPage } from "@/features/platform/pages/PlatformBillingRequestsPage";
import { platformSession } from "@/core/api/platformApi";
import { tenantFeaturesApi } from "@/core/api/tenantFeaturesApi";
import type { TenantFeatureCode } from "@/core/types/features";

const RequireAuth = (): React.ReactElement => {
  const status = useAuthStore((state) => state.status);
  const accessToken = useAuthStore((state) => state.accessToken);

  if (status === "idle" || status === "bootstrapping") return <LoadingScreen />;
  if (status !== "authenticated" || !accessToken)
    return <Navigate to="/login" replace />;
  return <Outlet />;
};

const PublicOnly = (): React.ReactElement => {
  const status = useAuthStore((state) => state.status);
  if (status === "idle" || status === "bootstrapping") return <LoadingScreen />;
  return status === "authenticated" ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Outlet />
  );
};

const RequireManager = (): React.ReactElement => {
  const role = useAuthStore((state) => state.role);
  return role === "manager" ? <Outlet /> : <Navigate to="/403" replace />;
};

const RequireLeadRead = (): React.ReactElement => {
  const permissions = useAuthStore((state) => state.permissions);
  return hasPermission(permissions, "leads.read") ? (
    <Outlet />
  ) : (
    <Navigate to="/403" replace />
  );
};

const RequirePermission = ({ code }: { code: string }): React.ReactElement => {
  const permissions = useAuthStore((state) => state.permissions);
  return hasPermission(permissions, code) ? (
    <Outlet />
  ) : (
    <Navigate to="/403" replace />
  );
};

const RequireTenantFeature = ({
  code,
}: {
  code: TenantFeatureCode;
}): React.ReactElement => {
  const featuresQuery = useQuery({
    queryKey: ["tenant-features"],
    queryFn: tenantFeaturesApi.current,
  });
  if (featuresQuery.isLoading) return <LoadingScreen />;
  if (
    featuresQuery.isError ||
    featuresQuery.data?.some(
      (feature) => feature.code === code && feature.isEnabled,
    )
  )
    return <Outlet />;
  return <Navigate to="/dashboard" replace />;
};

const RequirePlatformSession = (): React.ReactElement =>
  platformSession.hasToken() ? (
    <Outlet />
  ) : (
    <Navigate to="/platform/login" replace />
  );

const RouterFailure = (): React.ReactElement => (
  <Result
    status="500"
    title="خطا در مسیریابی برنامه"
    subTitle="صفحه موردنظر در حال حاضر قابل نمایش نیست."
    extra={
      <Button onClick={() => window.location.assign("/")}>
        بازگشت به صفحه اصلی
      </Button>
    }
  />
);

export const router = createBrowserRouter([
  {
    path: "/p/:tenantCode",
    element: <PublicOrganizationPage />,
    errorElement: <RouterFailure />,
  },
  {
    path: "/l/:token",
    element: <PublicPersonalLandingPage />,
    errorElement: <RouterFailure />,
  },
  {
    path: "/receipt/:token",
    element: <PublicRetailReceiptPage />,
    errorElement: <RouterFailure />,
  },
  {
    path: "/lead-capture/:code",
    element: <PublicLeadCapturePage />,
    errorElement: <RouterFailure />,
  },
  { path: "/platform/login", element: <PlatformLoginPage /> },
  {
    element: <RequirePlatformSession />,
    children: [
      {
        element: <PlatformShell />,
        children: [
          {
            path: "/platform",
            element: <Navigate to="/platform/tenants" replace />,
          },
          { path: "/platform/tenants", element: <PlatformTenantsPage /> },
          { path: "/platform/billing", element: <PlatformBillingRequestsPage /> },
          { path: "/platform/base-data", element: <PlatformBaseDataPage /> },
          { path: "/platform/features", element: <PlatformFeaturesPage /> },
          { path: "/platform/guides", element: <PlatformGuidesPage /> },
          { path: "/platform/staff", element: <PlatformStaffPage /> },
          {
            path: "/platform/announcements",
            element: <PlatformAnnouncementsPage />,
          },
          { path: "/platform/support", element: <PlatformSupportPage /> },
          {
            path: "/platform/notifications",
            element: <PlatformNotificationsPage />,
          },
          {
            path: "/platform/message-templates",
            element: <PlatformMessageTemplatesPage />,
          },
          { path: "/platform/settings", element: <PlatformSettingsPage /> },
        ],
      },
    ],
  },
  {
    element: <PublicOnly />,
    errorElement: <RouterFailure />,
    children: [
      {
        path: "/login",
        element: (
          <AuthLayout>
            <LoginPage />
          </AuthLayout>
        ),
      },
    ],
  },
  {
    element: <RequireAuth />,
    errorElement: <RouterFailure />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: "/dashboard", element: <WorkspaceDashboardPage /> },
          { path: "/guides", element: <GuidanceCenterPage /> },
          {
            path: "/dashboard/management",
            element: <WorkspaceDashboardPage />,
          },
          { path: "/dashboard/employee", element: <WorkspaceDashboardPage /> },
          {
            element: <RequireTenantFeature code="LEADS" />,
            children: [
              {
                element: <RequireLeadRead />,
                children: [
                  { path: "/leads", element: <LeadsPage /> },
                  {
                    path: "/leads/intelligence",
                    element: <LeadIntelligencePage />,
                  },
                ],
              },
            ],
          },
          {
            element: <RequireTenantFeature code="CUSTOMERS" />,
            children: [
              {
                element: <RequirePermission code="contacts.read" />,
                children: [{ path: "/contacts", element: <ContactsPage /> }],
              },
            ],
          },
          {
            element: <RequireTenantFeature code="ORGANIZATIONS" />,
            children: [
              {
                element: <RequirePermission code="accounts.read" />,
                children: [{ path: "/accounts", element: <AccountsPage /> }],
              },
            ],
          },
          {
            element: <RequireTenantFeature code="ORGANIZATIONS" />,
            children: [
              {
                element: <RequirePermission code="staff.manage" />,
                children: [{ path: "/team", element: <TeamPage /> }],
              },
              {
                element: <RequirePermission code="staff.manage" />,
                children: [{ path: "/roles", element: <TenantRolesPage /> }],
              },
            ],
          },
          {
            element: <RequirePermission code="activities.read" />,
            children: [{ path: "/activities", element: <ActivitiesPage /> }],
          },
          {
            element: <RequireTenantFeature code="PIPELINES" />,
            children: [
              {
                element: <RequirePermission code="opportunities.read" />,
                children: [
                  { path: "/opportunities", element: <OpportunitiesPage /> },
                ],
              },
            ],
          },
          {
            element: <RequireTenantFeature code="QUOTATION" />,
            children: [
              {
                element: <RequirePermission code="opportunities.read" />,
                children: [{ path: "/b2b-sales", element: <B2bSalesPage /> }],
              },
            ],
          },
          {
            element: <RequireTenantFeature code="CAMPAIGNS" />,
            children: [
              {
                element: <RequirePermission code="campaigns.read" />,
                children: [{ path: "/campaigns", element: <CampaignsPage /> }],
              },
            ],
          },
          {
            element: <RequireTenantFeature code="PRODUCTS" />,
            children: [
              {
                element: <RequireTenantFeature code="CUSTOMERS" />,
                children: [
                  {
                    element: <RequireTenantFeature code="CUSTOMER_INTEREST" />,
                    children: [
                      {
                        element: (
                          <RequireTenantFeature code="PERSONAL_LANDING" />
                        ),
                        children: [
                          {
                            element: <RequireTenantFeature code="SMS" />,
                            children: [
                              {
                                element: (
                                  <RequirePermission code="contacts.create" />
                                ),
                                children: [
                                  {
                                    path: "/retail",
                                    element: <RetailSalesPage />,
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            element: <RequireTenantFeature code="RESTAURANT_POS" />,
            children: [
              {
                element: <RequirePermission code="contacts.create" />,
                children: [{ path: "/restaurant", element: <RestaurantPosPage /> }],
              },
              {
                element: <RequireManager />,
                children: [{ path: "/restaurant/menu", element: <RestaurantMenuPage /> }],
              },
            ],
          },
          {
            element: <RequirePermission code="tenancy.manage" />,
            children: [{ path: "/billing", element: <TenantBillingPage /> }],
          },
          {
            element: <RequireManager />,
            children: [
              {
                element: <RequireTenantFeature code="ORGANIZATIONS" />,
                children: [
                  { path: "/organization", element: <OrganizationPage /> },
                ],
              },
              {
                element: <RequireTenantFeature code="PRODUCTS" />,
                children: [{ path: "/catalog", element: <CatalogPage /> }],
              },
              {
                element: <RequireTenantFeature code="PERSONAL_LANDING" />,
                children: [
                  {
                    path: "/reservation-settings",
                    element: <ReservationSettingsPage />,
                  },
                ],
              },
              {
                element: <RequireTenantFeature code="PRODUCTS" />,
                children: [
                  {
                    path: "/retail-settings",
                    element: <RetailSaleSettingsPage />,
                  },
                ],
              },
              {
                element: <RequireTenantFeature code="PRODUCTS" />,
                children: [
                  {
                    element: <RequireTenantFeature code="CUSTOMERS" />,
                    children: [
                      { path: "/loyalty", element: <RetailLoyaltyPage /> },
                    ],
                  },
                ],
              },
              {
                element: <RequireTenantFeature code="CAMPAIGNS" />,
                children: [
                  { path: "/automations", element: <AutomationsPage /> },
                ],
              },
              {
                element: <RequireTenantFeature code="SMS" />,
                children: [
                  { path: "/customer-sms", element: <CustomerSmsPage /> },
                  { path: "/support", element: <TenantSupportPage /> },
                ],
              },
            ],
          },
          { path: "/profile", element: <ProfilePage /> },
          { path: "/403", element: <ForbiddenPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
